import React, { useEffect, useState } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'

interface InputDateProps {
  register?: UseFormRegisterReturn
  value?: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  className?: string
  placeholder?: string
  error?: { message?: string } | boolean
  disabled?: boolean
  showError?: boolean // Se false, não exibe mensagem de erro (útil quando usado dentro de FormField)
}

/**
 * Componente InputDate totalmente controlado pelo React Hook Form.
 * 
 * - Usa validação nativa do HTML (min/max) e do React Hook Form
 * - Não bloqueia digitação manual do usuário
 * - Não muta o DOM diretamente
 * - Mantém acessibilidade padrão do input nativo
 */
type ResultadoValidacaoData = {
  valida: boolean
  normalizada?: string
}

const ANO_MINIMO = 1900
const ANO_MAXIMO = 2100

const limitarAno = (valor: string): string => {
  if (!valor)
    return valor

  const separador = valor.includes('/') ? '/' : valor.includes('-') ? '-' : ''
  if (!separador)
  {
    // Apenas dígitos (ex.: ano sendo digitado): limita a 4
    if (/^\d+$/.test(valor) && valor.length > 4)
      return valor.slice(0, 4)
    return valor
  }

  const partes = valor.split(separador)
  if (partes.length !== 3)
    return valor

  if (separador === '-')
  {
    if (partes[0].length > 4)
      partes[0] = partes[0].slice(0, 4)
  }
  else
  {
    if (partes[2].length > 4)
      partes[2] = partes[2].slice(0, 4)
  }

  return partes.join(separador)
}

const isDataValida = (ano: number, mes: number, dia: number): boolean => {
  if (ano < ANO_MINIMO || ano > ANO_MAXIMO)
  {
    return false
  }

  if (mes < 1 || mes > 12)
  {
    return false
  }

  if (dia < 1 || dia > 31)
  {
    return false
  }

  const data = new Date(Date.UTC(ano, mes - 1, dia))
  return data.getUTCFullYear() === ano
    && data.getUTCMonth() + 1 === mes
    && data.getUTCDate() === dia
}

const validarData = (valor: string): ResultadoValidacaoData => {
  if (!valor || valor.trim() === '')
  {
    return { valida: true, normalizada: '' }
  }

  const texto = valor.trim()
  const separador = texto.includes('/') ? '/' : texto.includes('-') ? '-' : ''
  if (!separador)
  {
    return { valida: false }
  }

  let ano = 0
  let mes = 0
  let dia = 0

  if (separador === '/')
  {
    const match = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (!match)
    {
      return { valida: false }
    }
    dia = Number(match[1])
    mes = Number(match[2])
    ano = Number(match[3])
  }
  else
  {
    const match = texto.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    if (!match)
    {
      return { valida: false }
    }
    ano = Number(match[1])
    mes = Number(match[2])
    dia = Number(match[3])
  }

  if (!isDataValida(ano, mes, dia))
  {
    return { valida: false }
  }

  const normalizada = `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
  return { valida: true, normalizada }
}

export const InputDate: React.FC<InputDateProps> = ({
  register,
  value,
  onChange,
  onBlur,
  className = '',
  placeholder,
  error = false,
  disabled = false,
  showError = true // Por padrão, exibe erro quando não está dentro de FormField
}) => {
  const [erroInterno, setErroInterno] = useState<string | null>(null)

  // Determinar se há erro e a mensagem
  const hasErrorExterno = typeof error === 'object' ? !!error : error
  const errorMessageExterno = typeof error === 'object' ? error?.message : undefined
  const hasError = hasErrorExterno || !!erroInterno
  const errorMessage = errorMessageExterno || erroInterno || undefined
  const registerProps = register ?? undefined
  /** Só usar valor controlado quando a prop existir; omitir `value` com RHF evita travar inputs (pendência → setValue na URL). */
  const valorControlado = value !== undefined
  const valorAtual = valorControlado ? value : ''

  useEffect(() => {
    if (!valorAtual || valorAtual.trim() === '')
    {
      setErroInterno(null)
      return
    }

    const resultado = validarData(valorAtual)
    if (resultado.valida)
      setErroInterno(null)
    else
      setErroInterno('Data inválida')
  }, [valorAtual])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valorOriginal = event.target.value
    const valorLimitado = limitarAno(valorOriginal)

    if (valorLimitado !== valorOriginal)
      event.target.value = valorLimitado

    const valorEnviar = valorLimitado
    if (register?.onChange)
      register.onChange({ ...event, target: { ...event.target, value: valorEnviar } } as React.ChangeEvent<HTMLInputElement>)
    if (onChange)
      onChange(valorEnviar)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const resultado = validarData(event.target.value)
    let valorFinal = event.target.value

    if (!resultado.valida)
    {
      valorFinal = ''
      event.target.value = ''
      setErroInterno('Data inválida')
    }
    else if (resultado.normalizada && resultado.normalizada !== event.target.value)
    {
      valorFinal = resultado.normalizada
      event.target.value = resultado.normalizada
    }
    else
    {
      setErroInterno(null)
    }

    if (register?.onChange)
    {
      register.onChange({
        target: {
          name: event.target.name,
          value: valorFinal
        }
      } as React.ChangeEvent<HTMLInputElement>)
    }

    if (onChange)
    {
      onChange(valorFinal)
    }

    if (register?.onBlur)
    {
      register.onBlur(event)
    }

    if (onBlur)
    {
      onBlur(valorFinal)
    }
  }

  return (
    <div>
      <input
        type="date"
        {...registerProps}
        onChange={handleChange}
        onBlur={handleBlur}
        {...(valorControlado ? { value: value ?? '' } : {})}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${
          hasError ? 'border-red-500' : 'border-gray-300'
        } ${className}`}
      />
      {/* Espaço fixo para a mensagem de erro, evita deslocar outros campos */}
      {showError && (
        <div className="mt-1 min-h-[1.25rem]">
          {errorMessage && <p className="text-red-500 text-sm">{errorMessage}</p>}
        </div>
      )}
    </div>
  )
}
